import type {ChangeEvent, FormEvent, ReactElement} from 'react';
import {useState} from 'react';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {Button, ButtonType, ButtonSize} from '@Components/button';
import {Text, TextSize} from '@Components/text';
import type {CheckoutStepProps} from '@Components/checkout-flow/checkout-flow.types';
import {CheckoutStep, US_COUNTRY_CODE, US_STATE_UNSELECTED_VALUE} from '@Components/checkout-flow/checkout-flow.types';
import {addBillingAddress} from '@Components/checkout-flow/checkout-flow-thunk';
import {CheckoutInfoMessage} from '@Components/checkout-flow/components/checkout-info-message/checkout-info-message';
import {CheckoutInfoMessageTone} from '@Components/checkout-flow/components/checkout-info-message/checkout-info-message.types';
import type {BillingAddressFormField, BillingAddressFormValues} from '@Components/checkout-flow/components/billing-address-form-step/billing-address-form-step.types';
import {
  BILLING_ADDRESS_FORM_INITIAL_VALUES,
  BILLING_ADDRESS_FORM_MAX_LENGTHS,
  getInvalidBillingAddressFields,
  toAddBillingAddressPayload,
} from '@Components/checkout-flow/components/billing-address-form-step/billing-address-form-step.types';
import styles from './billing-address-form-step.module.scss';

export function BillingAddressFormStep({controller}: CheckoutStepProps): ReactElement | null {
  const dispatch = useAppDispatch();
  const dialogData = useAppSelector((state) => state.checkoutFlow.dialogData);
  const hasAddresses = useAppSelector((state) => state.checkoutFlow.addresses.length > 0);
  const [values, setValues] = useState<BillingAddressFormValues>(BILLING_ADDRESS_FORM_INITIAL_VALUES);
  const [invalidFields, setInvalidFields] = useState<BillingAddressFormField[]>([]);
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (dialogData === null) {
    return null;
  }

  const isUnitedStates = values.countryCode === US_COUNTRY_CODE;

  const setField = (field: BillingAddressFormField, value: string): void => {
    setValues((current) => ({...current, [field]: value}));
    setInvalidFields((current) => current.filter((invalidField) => invalidField !== field));
  };

  const onInputChange = (field: BillingAddressFormField) => {
    return (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
      setField(field, event.target.value);
    };
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }
    setErrorMessage('');
    const invalid = getInvalidBillingAddressFields(values);
    if (invalid.length > 0) {
      setInvalidFields(invalid);
      setErrorMessage(window.i18next.t('pmwjs_address_validation_error_message'));
      return;
    }

    setIsSubmitting(true);
    try {
      await dispatch(addBillingAddress(toAddBillingAddressPayload(values))).unwrap();
      controller.goToStep(CheckoutStep.BILLING_ADDRESS_LIST);
    } catch {
      setErrorMessage(window.i18next.t('pmwjs_error_processing_request_try_again'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCancel = (): void => {
    controller.goToStep(hasAddresses ? CheckoutStep.BILLING_ADDRESS_LIST : CheckoutStep.PAYMENT_OPTIONS);
  };

  const getFieldClass = (field: BillingAddressFormField): string => {
    return `${styles.input} ${invalidFields.includes(field) ? styles.inputInvalid : ''}`;
  };

  const getTextInput = (field: BillingAddressFormField, label: string, autoComplete: string, placeholder = ''): ReactElement => {
    const inputId = `checkout-address-${field}`;
    return (
      <div className={styles.field}>
        <label htmlFor={inputId}>
          <Text val={label} size={TextSize.SM} bold className="content-body spacing-m-b-1" />
        </label>
        <input
          id={inputId}
          className={getFieldClass(field)}
          value={values[field]}
          onChange={onInputChange(field)}
          autoComplete={autoComplete}
          placeholder={placeholder}
          maxLength={BILLING_ADDRESS_FORM_MAX_LENGTHS[field]}
          type="text"
        />
      </div>
    );
  };

  const getRegionField = (): ReactElement => {
    if (!isUnitedStates) {
      return getTextInput('region', window.i18next.t('pmwjs_checkout_state_province_region'), 'address-level1');
    }
    return (
      <div className={styles.field}>
        <label htmlFor="checkout-address-us-state">
          <Text val={window.i18next.t('pmwjs_checkout_state')} size={TextSize.SM} bold className="content-body spacing-m-b-1" />
        </label>
        <select id="checkout-address-us-state" className={getFieldClass('usState')} value={values.usState} onChange={onInputChange('usState')} autoComplete="address-level1">
          <option value={US_STATE_UNSELECTED_VALUE}>{window.i18next.t('pmwjs_checkout_select_state')}</option>
          {Object.entries(dialogData.addressFormOptions.usStates).map(([code, name]) => {
            return (
              <option key={code} value={code}>
                {name}
              </option>
            );
          })}
        </select>
      </div>
    );
  };

  const getErrorMessage = (): ReactElement | null => {
    if (errorMessage === '') {
      return null;
    }
    return <CheckoutInfoMessage message={errorMessage} tone={CheckoutInfoMessageTone.DANGER} />;
  };

  return (
    <form
      className={styles.form}
      onSubmit={(event): void => {
        void onSubmit(event);
      }}
      noValidate
    >
      {getTextInput('contactName', window.i18next.t('pmwjs_checkout_contact_name'), 'name')}
      {getTextInput('streetAddress1', window.i18next.t('pmwjs_checkout_address_line_1'), 'address-line1', window.i18next.t('pmwjs_checkout_street_address_company_name'))}
      {getTextInput('streetAddress2', window.i18next.t('pmwjs_checkout_address_line_2'), 'address-line2', window.i18next.t('pmwjs_checkout_apartment_suite_unit_etc'))}
      <div className={styles.dualRow}>
        {getTextInput('city', window.i18next.t('pmwjs_checkout_city'), 'address-level2')}
        <div className={styles.field}>
          <label htmlFor="checkout-address-country-code">
            <Text val={window.i18next.t('pmwjs_checkout_country')} size={TextSize.SM} bold className="content-body spacing-m-b-1" />
          </label>
          <select id="checkout-address-country-code" className={getFieldClass('countryCode')} value={values.countryCode} onChange={onInputChange('countryCode')} autoComplete="country">
            <option value="">{window.i18next.t('pmwjs_checkout_select_country')}</option>
            {Object.entries(dialogData.addressFormOptions.countries).map(([code, name]) => {
              return (
                <option key={code} value={code}>
                  {name}
                </option>
              );
            })}
          </select>
        </div>
      </div>
      <div className={styles.dualRow}>
        {getRegionField()}
        {getTextInput('postalCode', window.i18next.t('pmwjs_checkout_postal_code'), 'postal-code')}
      </div>
      {getTextInput('phone', window.i18next.t('pmwjs_checkout_phone_number'), 'tel')}
      {getErrorMessage()}
      <div className={styles.actions}>
        <Button type={ButtonType.SECONDARY} size={ButtonSize.LARGE} text={window.i18next.t('pmwjs_cancel')} disabled={isSubmitting} onClick={onCancel} />
        <Button type={ButtonType.PRIMARY} size={ButtonSize.LARGE} text={window.i18next.t('pmwjs_checkout_add_address')} isLoading={isSubmitting} disabled={isSubmitting} attributes={{type: 'submit'}} />
      </div>
    </form>
  );
}
